import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AdminLayout } from "@/components/admin-layout";
import { Users, Building2, CreditCard, DollarSign, TrendingUp, AlertCircle, BarChart3, PieChart, Activity } from "lucide-react";
import { Link } from "wouter";
import { LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart as RechartsPieChart, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Pie } from 'recharts';
import type { AdminAnalytics } from '@/types/admin';

interface AdminStats {
  totalUsers: number;
  totalBusinesses: number;
  activeSubscriptions: number;
  monthlyRevenue: number;
  recentSignups: number;
  supportTickets: number;
}

interface Business {
  id: number;
  name: string;
  userId: number;
  userName: string;
  userEmail: string;
  subscription: {
    planName: string;
    status: string;
    startDate: string;
    isTrial?: boolean;
  };
  lastActivity: string;
}

export default function AdminDashboard() {
  const [timeframe, setTimeframe] = useState("30d");

  // Fetch admin statistics
  const { data: stats, isLoading: statsLoading } = useQuery<AdminStats>({
    queryKey: ['/api/admin/stats', timeframe],
    queryFn: async () => {
      const response = await fetch(`/api/admin/stats?timeframe=${timeframe}`);
      if (!response.ok) throw new Error('Failed to fetch admin stats');
      return response.json();
    },
  });

  // Fetch recent businesses for overview
  const { data: businesses, isLoading: businessesLoading } = useQuery<Business[]>({
    queryKey: ['/api/admin/businesses/recent'],
    queryFn: async () => {
      const response = await fetch('/api/admin/businesses/recent?limit=10');
      if (!response.ok) throw new Error('Failed to fetch recent businesses');
      return response.json();
    },
  });

  // Fetch analytics data for charts
  const { data: analyticsData } = useQuery<AdminAnalytics>({
    queryKey: ['/api/admin/analytics'],
  });

  const defaultStats: AdminStats = {
    totalUsers: 0,
    totalBusinesses: 0,
    activeSubscriptions: 0,
    monthlyRevenue: 0,
    recentSignups: 0,
    supportTickets: 0,
  };

  const currentStats = stats || defaultStats;

  // Color schemes for charts
  const COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4'];
  
  // Prepare chart data with fallbacks
  const monthlyRevenueData = analyticsData?.monthlyRevenue || [];
  const businessGrowthData = analyticsData?.businessGrowth || [];
  const industryDistribution = analyticsData?.industryDistribution || [];
  const transactionVolumeData = analyticsData?.transactionVolume || [];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
            <p className="text-muted-foreground">
              Manage your ZentroBooks platform and client accounts
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link href="/dashboard/client-management">
                <Users className="h-4 w-4 mr-2" />
                Client Management
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link href="/reports/jasper">
                <TrendingUp className="h-4 w-4 mr-2" />
                Advanced Reports
              </Link>
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {statsLoading ? "..." : currentStats.totalUsers.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">
                +{currentStats.recentSignups} new this month
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Businesses</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {statsLoading ? "..." : currentStats.totalBusinesses.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">
                Across all client accounts
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Subscriptions</CardTitle>
              <CreditCard className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {statsLoading ? "..." : currentStats.activeSubscriptions.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">
                Paying customers
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {statsLoading ? "..." : `$${currentStats.monthlyRevenue.toLocaleString()}`}
              </div>
              <p className="text-xs text-muted-foreground">
                Recurring revenue
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 1 - Revenue and Business Growth */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Monthly Revenue Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Monthly Revenue Trend
              </CardTitle>
              <CardDescription>
                Revenue growth over the last 12 months
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={monthlyRevenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`$${value}`, 'Revenue']} />
                  <Area type="monotone" dataKey="revenue" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.2} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Business Growth Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Business Registration Growth
              </CardTitle>
              <CardDescription>
                New businesses joining the platform
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={businessGrowthData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="newBusinesses" stroke="#10b981" strokeWidth={3} name="New Businesses" />
                  <Line type="monotone" dataKey="totalBusinesses" stroke="#3b82f6" strokeWidth={2} strokeDasharray="5 5" name="Total Businesses" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 2 - Industry and Transaction Volume */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Industry Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="h-5 w-5" />
                Industry Distribution
              </CardTitle>
              <CardDescription>
                Breakdown of businesses by industry type
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPieChart>
                  <Pie
                    data={industryDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {industryDistribution.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RechartsPieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Transaction Volume */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Daily Transaction Volume
              </CardTitle>
              <CardDescription>
                Transaction activity over the last 30 days
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={transactionVolumeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Recent Client Activity</CardTitle>
              <CardDescription>
                Latest businesses and user signups
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {businessesLoading ? (
                  <div className="text-center py-4">Loading...</div>
                ) : businesses && businesses.length > 0 ? (
                  businesses.map((business: Business) => (
                    <div key={business.id} className="flex items-center justify-between">
                      <div className="space-y-1">
                        <p className="text-sm font-medium">{business.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {business.userEmail}
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge variant="secondary" className="text-xs">
                          {business.subscription?.isTrial ? "Free Trial" : (business.subscription?.planName || "Free")}
                        </Badge>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(business.lastActivity).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    No recent activity
                  </div>
                )}
              </div>
              <div className="mt-4">
                <Button variant="outline" className="w-full" asChild>
                  <Link href="/dashboard/client-management">
                    View All Clients
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>
                Common administrative tasks
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Button variant="outline" className="w-full justify-start" asChild>
                  <Link href="/dashboard/client-management">
                    <Users className="h-4 w-4 mr-2" />
                    Manage Client Accounts
                  </Link>
                </Button>
                
                <Button variant="outline" className="w-full justify-start" asChild>
                  <Link href="/subscription/manage-plans">
                    <CreditCard className="h-4 w-4 mr-2" />
                    Subscription Plans
                  </Link>
                </Button>
                
                <Button variant="outline" className="w-full justify-start" asChild>
                  <Link href="/reports/jasper">
                    <TrendingUp className="h-4 w-4 mr-2" />
                    Advanced Reports
                  </Link>
                </Button>
                
                <Button variant="outline" className="w-full justify-start" asChild>
                  <Link href="/admin/support">
                    <AlertCircle className="h-4 w-4 mr-2" />
                    Support Center
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* System Health */}
        <Card>
          <CardHeader>
            <CardTitle>Platform Overview</CardTitle>
            <CardDescription>
              System status and key metrics
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <p className="text-sm font-medium">System Status</p>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-green-500"></div>
                  <span className="text-sm text-muted-foreground">All systems operational</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm font-medium">Database</p>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-green-500"></div>
                  <span className="text-sm text-muted-foreground">Connected and healthy</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm font-medium">API Status</p>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-green-500"></div>
                  <span className="text-sm text-muted-foreground">All endpoints responding</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
